import Vue from 'vue'
import axios from '../config/api'
import router from '@/router'
import { useAxiosErrorHandler } from '@/composables/useAxiosErrorHandler'

const campaignType = process.env.VUE_APP_CAMPAIGN_TYPE ?? 'BNN'

const state = () => ({
  data: [],
  paymentId: null,
  installmentPlan: null,
  payload: null,
  isError: false,
  isLoading: false,
  edc: {
    code: [],
    choices: [],
    promotions: [],
    order_types: [],
    pay_at_store_choices: [],
    card_types: [],
    isSuccess: false,
    isError: false,
    isLoading: false,
  },
  voucher: {
    data: {},
    code: '',
    isSuccess: false,
    isError: false,
    errorMessage: '',
    isLoading: false,
  },
  guaranteePaymentMedthods: [
    // TODO: [Guarantee Paymeny] Integrate with the API when available to fetch guarantee payment methods
    {
      id: 4,
      name: 'K-Bank',
      type: 'full-payment',
      code: 'KBF-001',
      title: 'ชำระเงินด้วยบัตร VISA/Master Card',
      short_description: null,
      on_top_discount_percent: 0,
      dropdown: null,
    },
  ],
  productPaymentOptions: {
    data: [],
    isLoading: false,
    isError: false,
    errorMessage: '',
  },
  techswop: {
    validate: {
      data: null,
      success: null,
      message: '',
      isLoading: false,
      isError: false,
      errorMessage: '',
    },
    switchBranch: {
      data: null,
      success: null,
      message: '',
      isLoading: false,
      isError: false,
      errorMessage: '',
    },
    confirm: {
      data: null,
      success: null,
      message: '',
      isLoading: false,
      isError: false,
      errorMessage: '',
    },
    changePaymentType: {
      data: null,
      success: null,
      message: '',
      isLoading: false,
      isError: false,
      errorMessage: '',
    },
  },
})

// getters
const getters = {
  getPaymentTypeById: state => id => {
    return state.data?.find(i => i?.id === id) ?? null
  },

  getCurrentPaymentType: state => {
    return state.data?.find(i => i?.id === state.paymentId) ?? null
  },

  getCardTypesByPayAtStoreChoiceId: state => id => {
    const payAtStoreChoiceSelected = state.edc.pay_at_store_choices.find(
      i => i.id === id,
    )

    if (!payAtStoreChoiceSelected) {
      return []
    }

    return state.edc.card_types.filter(
      i => i.payment_type === payAtStoreChoiceSelected.payment_type,
    )
  },

  getPaymentTypeByPayAtStoreChoiceId: state => id => {
    return (
      state.edc.pay_at_store_choices.find(i => i.id === id) ?? null
    )
  },

  getGuaranteePaymentMethods: state => {
    return state.guaranteePaymentMedthods ?? []
  },

  getProductPaymentOptions: state => {
    return state.productPaymentOptions.data ?? {}
  },
}

// actions
const actions = {
  async getPaymentType({ commit }, id) {
    //  if (state.data.length === 0) {
    try {
      const response = await axios.get(
        `/v2/payment-types/?campaign_id=${id}`,
      )
      commit('setPaymentTypeSuccess', response.data.data)
    } catch {
      commit('setPaymentTypeError')
    }
    // }
  },

  async getEdcByBranch({ commit }, params) {
    commit('setEdcBranchRequest')

    try {
      const response = await axios.get(`/branche-edc`, {
        params,
      })

      commit('setEdcBranchSuccess', response.data.data)
    } catch {
      commit('setEdcBranchError')
    }
  },

  async verifyVoucher({ commit }, payload) {
    commit('setVerifyVoucherRequest', payload)

    const accessToken = Vue.prototype.$cookies.get('token')

    if (!accessToken) {
      Vue.$toast.error('กรุณาเข้าสู่ระบบ', {
        duration: 5000,
        position: 'top',
      })
      return
    }

    try {
      const response = await axios.post(
        '/orders/verify-voucher',
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      commit('setVerifyVoucherSuccess', response.data.data)
    } catch (error) {
      const message = useAxiosErrorHandler().handleError(error)
      commit('setVerifyVoucherError', message)
    }
  },

  async fetchProductPaymentOptions({ commit }, payload) {
    commit('setProductPaymentOptionsRequest')

    try {
      const response = await axios.post(
        `/v2/${campaignType}/product-payment-options`,
        payload,
      )
      commit('setProductPaymentOptionsSuccess', response.data.data)
    } catch (error) {
      const message = useAxiosErrorHandler().handleError(error)
      commit('setProductPaymentOptionsError', message)
    }
  },

  async validateTechswop({ commit }, payload) {
    commit('setValidateTechswopRequest')

    try {
      const response = await axios.post(
        '/orders/tradein/validate-techswop',
        payload,
      )

      const transformed = {
        ...response.data.data,
        vouchers: response.data.data?.vouchers.map(voucher => ({
          ...voucher,
          code: voucher.input,
        })),
      }

      commit('setValidateTechswopSuccess', {
        data: transformed,
        success: response.data.success,
        message: response.data.message,
      })
    } catch (error) {
      const message = useAxiosErrorHandler().handleError(error)
      commit('setValidateTechswopError', message)
    }
  },

  async switchTechswopBranch({ commit }, payload) {
    commit('setSwitchTechswopBranchRequest')

    try {
      const response = await axios.post(
        '/orders/tradein/switch-techswop-branch',
        payload,
      )

      const transformed = {
        ...response.data.data,
        vouchers: response.data.data?.vouchers.map(voucher => ({
          ...voucher,
          code: voucher.input,
        })),
      }

      commit('setSwitchTechswopBranchSuccess', {
        data: response.data.data,
        success: response.data.success,
        message: response.data.message,
      })

      commit('setValidateTechswopSuccess', {
        data: transformed,
        success: response.data.success,
        message: response.data.message,
      })
    } catch (error) {
      const message = useAxiosErrorHandler().handleError(error)
      commit('setSwitchTechswopBranchError', message)
    }
  },

  async changeTradeInPaymentMethod({ commit }, payload) {
    commit('setChangeTradeInPaymentMethodRequest')

    try {
      const response = await axios.post(
        '/orders/tradein/change-payment-type',
        payload,
      )
      commit('setChangeTradeInPaymentMethodSuccess', {
        data: response.data.data,
        success: response.data.success,
        message: response.data.message,
      })

      // after successfully change payment method, update the order payment method in the order store
      const {
        payment_type_id,
        payment_type,
        payment_selected,
        payment_code,
      } = response.data.data

      commit(
        'order/setOrderPaymentMethod',
        {
          payment_type_id,
          payment_type,
          payment_selected,
          payment_code,
        },
        {
          root: true,
        },
      )
    } catch (error) {
      const message = useAxiosErrorHandler().handleError(error)
      commit('setChangeTradeInPaymentMethodError', message)
    }
  },

  async confirmTechswop({ commit }, payload) {
    commit('setConfirmTechswopRequest')

    try {
      const response = await axios.post(
        '/orders/tradein/confirm-techswop',
        payload,
      )
      commit('setConfirmTechswopSuccess', {
        data: response.data.data,
        success: response.data.success,
        message: response.data.message,
      })
    } catch (error) {
      const message = useAxiosErrorHandler().handleError(error)
      commit('setConfirmTechswopError', message)
    }
  },

  async getTradeInPaymentMethods({ commit }, payload) {
    try {
      const response = await axios.post(
        '/orders/tradein/payment-options',
        payload,
      )
      commit(
        'setPaymentTypeSuccess',
        response.data.data.available_payment_types,
      )
      return response.data.data
    } catch {
      commit('setPaymentTypeError')
    }
  },

  async getVipPaymentMethods({ commit }, uuid) {
    // isLoading
    commit('setPaymentType')

    try {
      const response = await axios.get(`/vip-payment/${uuid}`)

      // order
      commit(
        'order/setOrderSuccess',
        {
          ...response.data.data?.order,
          payment_opens_at:
            response.data.data?.payment_opens_at ?? null,
          payment_opens_at_timestamp:
            response.data.data?.payment_opens_at_unix ?? null,
          is_payment_open:
            response.data.data?.is_payment_open ?? false,
        } ?? {},
        { root: true },
      )

      // pickup branch: available_branch_times
      commit(
        'branchStore/setBranchSuccess',
        response.data.data?.available_branch_times ?? [],
        { root: true },
      )

      // Payment methods: available_payment_gateways
      commit(
        'setPaymentTypeSuccess',
        response.data.data?.available_payment_gateways,
      )
    } catch (error) {
      const message = useAxiosErrorHandler().handleError(error)
      commit('branchStore/setBranchError', null, { root: true })
      commit('setPaymentTypeError', message)

      if (error.response && error.response.status > 400) {
        router.push('/404')
      }
    }
  },
}

// mutations
const mutations = {
  setPaymentId(state, payload) {
    state.paymentId = payload
  },
  setPaymentInstallmentPlan(state, payload) {
    state.installmentPlan = payload
  },
  setPaymentType(state) {
    state.isLoading = true
    state.isError = false
  },
  setPaymentTypeSuccess(state, payload) {
    state.isLoading = false
    state.data = payload
    state.isError = false
  },
  setPaymentTypeError(state) {
    state.isLoading = false
    state.isError = true
  },
  setEdcBranchRequest(state) {
    state.edc.isLoading = true
    state.edc.isError = false
  },
  setEdcBranchSuccess(state, payload) {
    state.edc.isLoading = false
    state.edc.code = payload.edc_code
    state.edc.order_types = payload.order_types
    state.edc.pay_at_store_choices = payload.pay_at_store_choices
    state.edc.card_types = payload.card_types
    state.edc.isError = false
  },
  setEdcBranchError(state) {
    state.edc.isLoading = false
    state.edc.isError = true
  },
  setVerifyVoucherRequest(state, payload) {
    state.voucher.code = payload.voucher_code ?? null
    state.voucher.isLoading = true
    state.voucher.isError = false
    state.voucher.errorMessage = ''
  },
  setVerifyVoucherSuccess(state, payload) {
    state.voucher.isLoading = false
    state.voucher.data = payload
    state.voucher.isSuccess = true
    state.voucher.isError = false
    state.voucher.errorMessage = ''
  },
  setVerifyVoucherError(state, payload) {
    state.voucher.isLoading = false
    state.voucher.isError = true
    state.voucher.isSuccess = false
    state.voucher.errorMessage =
      payload ?? 'ไม่สามารถใช้ Voucher นี้ได้'
  },
  setVerifyVoucherReset(state) {
    state.voucher.data = {}
    state.voucher.code = ''
    state.voucher.isLoading = false
    state.voucher.isSuccess = false
    state.voucher.isError = false
    state.voucher.errorMessage = ''
  },
  setProductPaymentOptionsRequest(state) {
    state.productPaymentOptions.isLoading = true
    state.productPaymentOptions.isError = false
    state.productPaymentOptions.errorMessage = ''
  },
  setProductPaymentOptionsSuccess(state, payload) {
    state.productPaymentOptions.isLoading = false
    state.productPaymentOptions.data = {
      ...payload,
      deposit_payment: payload?.deposit_payment == 1,
      full_payment: payload?.full_payment == 1,
      is_fullpay_pickup: payload?.is_fullpay_pickup == 1,
      is_fullpay_delivery: payload?.is_fullpay_delivery == 1,
    }
  },
  setProductPaymentOptionsError(state, errorMessage) {
    state.productPaymentOptions.isLoading = false
    state.productPaymentOptions.isError = true
    state.productPaymentOptions.errorMessage = errorMessage
  },
  setValidateTechswopRequest(state) {
    state.techswop.validate.isLoading = true
    state.techswop.validate.isError = false
    state.techswop.validate.errorMessage = ''
  },
  setValidateTechswopSuccess(state, { data, success, message }) {
    state.techswop.validate.isLoading = false
    state.techswop.validate.data = data
    state.techswop.validate.success = success
    state.techswop.validate.message = message
    state.techswop.validate.isError = false
    state.techswop.validate.errorMessage = ''
  },
  setValidateTechswopError(state, errorMessage) {
    state.techswop.validate.isLoading = false
    state.techswop.validate.isError = true
    state.techswop.validate.errorMessage = errorMessage
  },
  setValidateTechswopReset(state) {
    state.techswop.validate.data = null
    state.techswop.validate.success = null
    state.techswop.validate.message = ''
    state.techswop.validate.isLoading = false
    state.techswop.validate.isError = false
    state.techswop.validate.errorMessage = ''
  },
  setSwitchTechswopBranchRequest(state) {
    state.techswop.switchBranch.isLoading = true
    state.techswop.switchBranch.isError = false
    state.techswop.switchBranch.errorMessage = ''
  },
  setSwitchTechswopBranchSuccess(state, { data, success, message }) {
    state.techswop.switchBranch.isLoading = false
    state.techswop.switchBranch.data = data
    state.techswop.switchBranch.success = success
    state.techswop.switchBranch.message = message
    state.techswop.switchBranch.isError = false
    state.techswop.switchBranch.errorMessage = ''
  },
  setSwitchTechswopBranchError(state, errorMessage) {
    state.techswop.switchBranch.isLoading = false
    state.techswop.switchBranch.isError = true
    state.techswop.switchBranch.errorMessage = errorMessage
  },
  setSwitchTechswopBranchReset(state) {
    state.techswop.switchBranch.data = null
    state.techswop.switchBranch.success = null
    state.techswop.switchBranch.message = ''
    state.techswop.switchBranch.isLoading = false
    state.techswop.switchBranch.isError = false
    state.techswop.switchBranch.errorMessage = ''
  },
  setConfirmTechswopRequest(state) {
    state.techswop.confirm.isLoading = true
    state.techswop.confirm.isError = false
    state.techswop.confirm.errorMessage = ''
  },
  setConfirmTechswopSuccess(state, { data, success, message }) {
    state.techswop.confirm.isLoading = false
    state.techswop.confirm.data = data
    state.techswop.confirm.success = success
    state.techswop.confirm.message = message
    state.techswop.confirm.isError = false
    state.techswop.confirm.errorMessage = ''
  },
  setConfirmTechswopError(state, errorMessage) {
    state.techswop.confirm.isLoading = false
    state.techswop.confirm.isError = true
    state.techswop.confirm.errorMessage = errorMessage
  },
  setConfirmTechswopReset(state) {
    state.techswop.confirm.data = null
    state.techswop.confirm.success = null
    state.techswop.confirm.message = ''
    state.techswop.confirm.isLoading = false
    state.techswop.confirm.isError = false
    state.techswop.confirm.errorMessage = ''
  },
  setChangeTradeInPaymentMethodRequest(state) {
    state.techswop.changePaymentType.isLoading = true
    state.techswop.changePaymentType.isError = false
    state.techswop.changePaymentType.errorMessage = ''
  },
  setChangeTradeInPaymentMethodSuccess(
    state,
    { data, success, message },
  ) {
    state.techswop.changePaymentType.isLoading = false
    state.techswop.changePaymentType.data = data
    state.techswop.changePaymentType.success = success
    state.techswop.changePaymentType.message = message
    state.techswop.changePaymentType.isError = false
    state.techswop.changePaymentType.errorMessage = ''
  },
  setChangeTradeInPaymentMethodError(state, errorMessage) {
    state.techswop.changePaymentType.isLoading = false
    state.techswop.changePaymentType.isError = true
    state.techswop.changePaymentType.errorMessage = errorMessage
  },
  setChangeTradeInPaymentMethodReset(state) {
    state.techswop.changePaymentType.data = null
    state.techswop.changePaymentType.success = null
    state.techswop.changePaymentType.message = ''
    state.techswop.changePaymentType.isLoading = false
    state.techswop.changePaymentType.isError = false
    state.techswop.changePaymentType.errorMessage = ''
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
